import { Box, Flex, Text, IconButton } from "@chakra-ui/react";
import { useEffect, useState } from "react";

interface ToastProps {
  type?: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  onClose?: () => void;
  isVisible?: boolean;
}

function Toast({ 
  type = 'success', 
  title, 
  message, 
  duration = 3000, 
  onClose,
  isVisible = true 
}: ToastProps) {
  const [show, setShow] = useState(isVisible);

  useEffect(() => {
    setShow(isVisible);
  }, [isVisible]);

  useEffect(() => {
    if (show && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration]);

  const handleClose = () => {
    setShow(false);
    setTimeout(() => {
      onClose?.();
    }, 300); // 等待动画完成
  };

  const getTypeConfig = () => {
    switch (type) {
      case 'success':
        return {
          bgColor: 'rgba(45, 45, 55, 0.95)',
          iconColor: '#00C851',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="currentColor"/>
              <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        };
      case 'error':
        return {
          bgColor: 'rgba(45, 45, 55, 0.95)',
          iconColor: '#FF4444',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="currentColor"/>
              <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        };
      case 'warning':
        return {
          bgColor: 'rgba(45, 45, 55, 0.95)',
          iconColor: '#FF8800',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="currentColor"/>
              <path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        };
      case 'info':
        return {
          bgColor: 'rgba(45, 45, 55, 0.95)',
          iconColor: '#33B5E5',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="currentColor"/>
              <path d="M12 16v-4M12 8h.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        };
      default:
        return {
          bgColor: 'rgba(45, 45, 55, 0.95)',
          iconColor: '#00C851',
          icon: null
        };
    }
  };

  const config = getTypeConfig();

  if (!show) return null;

  return (
    <Box
      bg="rgba(40, 40, 50, 0.75)"
      borderRadius="8px"
      boxShadow="0 4px 20px rgba(0,0,0,0.25)"
      p={3}
      minW="200px"
      maxW="320px"
      style={{ 
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}
      opacity={show ? 1 : 0}
      transform={show ? 'translateX(0)' : 'translateX(-100%)'}
      transition="all 0.3s ease-in-out"
    >
      <Flex align="flex-start" gap={2}>
        {/* 图标 */}
        <Box color={config.iconColor} flexShrink={0}>
          {config.icon}
        </Box>
        
        {/* 内容 */}
        <Box flex={1}>
          <Text 
            color="white" 
            fontSize="13px" 
            fontWeight="600"
            mb={message ? 0.5 : 0}
          >
            {title}
          </Text>
          {message && (
            <Text 
              color="rgba(255, 255, 255, 0.8)" 
              fontSize="11px"
              lineHeight="1.3"
            >
              {message}
            </Text>
          )}
        </Box>
        
        {/* 关闭按钮 */}
        <IconButton
          aria-label="关闭"
          size="xs"
          variant="ghost"
          color="rgba(255, 255, 255, 0.6)"
          minW="auto"
          h="auto"
          p={1}
          _hover={{ 
            color: "white",
            bg: "rgba(255, 255, 255, 0.1)"
          }}
          onClick={handleClose}
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
        />
      </Flex>
    </Box>
  );
}

export default Toast; 